import { BehaviorSubject, distinctUntilChanged, map, type Observable, of, switchMap } from 'rxjs';

import { type ChatContext } from './chat-context.models';

export interface ChatContextSessionHandle<TState> {
  readonly state$: BehaviorSubject<TState>;
  close(): void;
}

export interface ChatContextSessionStack<TState> {
  readonly context$: Observable<ChatContext | null>;
  open(initialState: TState): ChatContextSessionHandle<TState>;
}

export function createChatContextSessionStack<TState, TContext extends ChatContext>(
  toContext: (state: TState) => TContext,
): ChatContextSessionStack<TState> {
  const sessions$ = new BehaviorSubject<readonly ChatContextSessionHandle<TState>[]>([]);

  const context$: Observable<ChatContext | null> = sessions$.pipe(
    map((sessions) => sessions.at(-1)),
    distinctUntilChanged(),
    switchMap((topSession) =>
      topSession ? topSession.state$.pipe(map(toContext)) : of<ChatContext | null>(null),
    ),
  );

  function open(initialState: TState): ChatContextSessionHandle<TState> {
    const state$ = new BehaviorSubject(initialState);
    const handle: ChatContextSessionHandle<TState> = {
      state$,
      close: () => sessions$.next(sessions$.getValue().filter((session) => session !== handle)),
    };
    sessions$.next([...sessions$.getValue(), handle]);
    return handle;
  }

  return { context$, open };
}
